import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import {
  NotificationActionTypeEnum,
  showCustomNotification,
  showError,
  showNotification,
} from '@vk-hr-tek/core/notifications';
import { FlowTreeNode } from '@vk-hr-tek/ui/FlowTree';

import {
  GetEventTypeWorkflowResponse,
  CompanyEventsStat,
  CompanyGroupUsers,
  GetNodeOptionsResponse,
  GetNewWorkflowParticipantOptionsResponse,
  GetCollectionOptionsResponse,
  SettingsService,
  UpdateNode,
  UpdateValidatorRequest,
  CompanyEventTypesCollectionOptionsParams,
  DeleteValidatorParams,
  DeleteValidatorRequest,
  CompanyEventTypesCollectionParams,
  UpdateCollectionsRequest,
} from '@app/gen/settings';
import { ThunkExtra } from '@app/store';
import { Entity } from '@app/types';
import {
  EventType,
  EventTypeService,
  GetCollectionResponse,
  GetCompanyCollectionsEventTypesResponse,
  GetCollectionsQuery,
} from '@app/gen/event-type';

import { SettingsWorkerWorkflowMapper } from '../../../mappers';
import { SettingsState, SettingsWithRootState } from '../../settings.state';
import {
  ActionEventTypeDto,
  AddParticipantDto,
  DeleteParticipantsDto,
  UpdateEventTypeDisabledDto,
  UpdateValidatorDto,
} from '../../../dto';
import { getCompanies } from '../companies';
interface DispatchResult<T> {
  type: string;
  payload: AppError | T | undefined;
}

const awaitDispatch = async <T>(
  promise: Promise<DispatchResult<T>>,
): Promise<T> => {
  const { type, payload } = await promise;

  if (type.includes('rejected')) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw payload;
  }

  return payload as T;
};

export const getEventType = createAsyncThunk<
  GetEventTypeWorkflowResponse & {
    companyName: string;
    treeData: FlowTreeNode;
  },
  ActionEventTypeDto,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getEventType',
  async (
    actionEventTypeDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      let companies = getState().settings?.companies;

      if (!companies?.length) {
        companies = await awaitDispatch(dispatch(getCompanies()));
      }

      const companyName =
        companies.find((i) => i.id === actionEventTypeDto.companyId)?.name ||
        '';

      if (!companyName) {
        throw new AppError('client', {
          code: 400,
          message: 'компании с таким id не существует',
        });
      }

      const eventTypeService = inject(SettingsService);
      const workflowService = inject(SettingsWorkerWorkflowMapper);
      const result = await eventTypeService.getEventTypeWorkflow({
        params: { event_type_id: actionEventTypeDto.eventTypeId },
      });
      const treeData = await workflowService.transform(result.workflow);
      return { ...result, companyName, treeData };
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEventTypeDisabled = createAsyncThunk<
  unknown,
  UpdateEventTypeDisabledDto,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateEventTypeDisabled',
  async (
    { eventTypeId, disabled, eventTypeName },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(SettingsService);
      await eventTypeService.updateEventType({
        params: { event_type_id: eventTypeId },
        body: { disabled },
      });

      dispatch(
        showCustomNotification([
          {
            title: `Тип заявки ${
              disabled ? 'скрыт' : 'виден'
            } в списке доступных при создании новой заявки`,
            message: eventTypeName,
            type: 'info',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            title: `Не удалось ${
              disabled ? 'выключить' : 'включить'
            } видимый тип заявки. Попробуйте, пожалуйста, еще раз`,
            message: eventTypeName,
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getEventTypeCollections = createAsyncThunk<
  GetCollectionOptionsResponse,
  CompanyEventTypesCollectionOptionsParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getEventTypeCollections',
  async ({ event_type_id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const eventTypeService = inject(SettingsService);
      const result = await eventTypeService.companyEventTypesCollectionOptions({
        params: { event_type_id },
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEventTypeCollections = createAsyncThunk<
  unknown,
  CompanyEventTypesCollectionParams &
    UpdateCollectionsRequest & { company_id: string },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateEventTypeCollections',
  async (
    { event_type_id, collection_ids, company_id },
    { dispatch, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(SettingsService);
      await eventTypeService.companyEventTypesCollection({
        params: { event_type_id },
        body: { collection_ids },
      });
      dispatch(
        getEventType({
          eventTypeId: event_type_id,
          companyId: company_id,
        }),
      );

      dispatch(
        showCustomNotification([
          {
            message: 'Коллекция успешно изменена!',
            type: 'info',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось изменить коллекцию',
            message: 'Попробуйте еще раз',
            type: 'error',
          },
        ]),
      );
    }
  },
);

export const deleteValidator = createAsyncThunk<
  void,
  DeleteValidatorParams & DeleteValidatorRequest & { company_id: string },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/deleteValidator',
  async (
    { event_type_id, validator_id, company_id },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(SettingsService);
      await eventTypeService.deleteValidator({
        params: { event_type_id },
        body: { validator_id },
      });
      dispatch(showNotification('Валидатор был удален'));
      dispatch(
        getEventType({ eventTypeId: event_type_id, companyId: company_id }),
      );
    } catch (err) {
      dispatch(showError('При удалении валидатора произошла ошибка'));
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateValidator = createAsyncThunk<
  void,
  {
    values: UpdateValidatorDto;
    actions: {
      resolve: (value: unknown) => void;
    };
    companyId: string;
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateValidator',
  async (
    { values, actions, companyId },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(SettingsService);

      const {
        eventTypeId,
        type,
        working_days_only: workingDaysOnly,
        days_count: daysCount,
        validator_id: validatorId,
        operation,
      } = values;

      let daysCountType = 'days_count';

      if (type === 'min_duration' || type === 'max_duration') {
        daysCountType = type;
      }

      await eventTypeService.updateValidator({
        params: { event_type_id: eventTypeId },
        body: {
          validator_id: validatorId,
          settings: {
            type,
            ...(type === 'min_hiring_day'
              ? { operation }
              : {
                  [daysCountType]: daysCount,
                  working_days_only: workingDaysOnly,
                }),
          } as UpdateValidatorRequest['settings'],
        },
      });
      actions.resolve(null);
      dispatch(showNotification('Валидатор был изменен'));
      dispatch(getEventType({ eventTypeId: values.eventTypeId, companyId }));
    } catch (err) {
      actions.resolve({ [FORM_ERROR]: err });
      dispatch(showError('При редактировании валидатора произошла ошибка'));
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateFlowNodes = createAsyncThunk<
  void,
  {
    eventTypeId: string;
    nodes: UpdateNode[];
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateFlowNodes',
  async (
    { eventTypeId, nodes, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const hasTypeUpdate = nodes.some((node) => 'manager_type' in node);

    try {
      const service = inject(SettingsService);

      await service.updateNodes({
        params: {
          event_type_id: eventTypeId,
        },
        body: {
          nodes,
        },
      });
      if (hasTypeUpdate) {
        dispatch(
          showCustomNotification([
            {
              message: 'Тип оргструктуры изменен',
              type: 'info',
              action: {
                actionType: NotificationActionTypeEnum.Redirect,
                text: 'Настроить импорт оргструктуры из 1С',
                link: '/company/settings/company',
              },
            },
          ]),
        );
      }
      dispatch(showNotification('Данные этапов были изменены'));
      actions.resolve(null);
    } catch (err) {
      actions.reject(err);
      if (hasTypeUpdate) {
        dispatch(
          showError(
            'Не удалось изменить тип оргструктуры!\nПопробуйте еще раз позже',
          ),
        );
      }
      dispatch(
        showError(
          'Не удалось изменить данные этапов!\nПопробуйте еще раз позже',
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getEventTypeNodeOptions = createAsyncThunk<
  GetNodeOptionsResponse,
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getEventTypeNodeOptions',
  async (id, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);
      const res = await service.getNodeOptions({
        params: { event_type_id: id },
      });

      return res;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getParticipantOptions = createAsyncThunk<
  GetNewWorkflowParticipantOptionsResponse,
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getParticipantOptions',
  async (id, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);
      const res = await service.getNewWorkflowParticipantOptions({
        params: { event_type_id: id },
      });

      return res;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const addParticipant = createAsyncThunk<
  void,
  {
    values: AddParticipantDto;
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/addParticipant',
  async (
    { values, actions },
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    const { eventTypeId, companyId, groupId } = values;

    const participantsOptions =
      getState().settings.eventType.participantsOptions;
    const label =
      participantsOptions?.find((option) => option.id === groupId)?.name || '';

    try {
      const service = inject(SettingsService);
      await service.addWorkflowParticipant({
        params: { event_type_id: eventTypeId },
        body: { group_id: groupId },
      });
      dispatch(
        showCustomNotification([
          {
            title: 'Участник успешно добавлен',
            subtitle: label,
            message: 'Наблюдатель',
            type: 'info',
          },
        ]),
      );
      dispatch(getEventType({ eventTypeId, companyId }));
      actions.resolve(null);
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(showError('Не удалось добавить участника'));
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const deleteParticipant = createAsyncThunk<
  void,
  {
    values: DeleteParticipantsDto;
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/deleteParticipant',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const { eventTypeId, companyId, role, group } = values;

    try {
      const service = inject(SettingsService);
      await service.deleteWorkflowParticipant({
        params: { event_type_id: eventTypeId },
        body: { participant_ids: values.participantIds },
      });
      dispatch(
        showCustomNotification([
          {
            title: 'Участник успешно удалён',
            subtitle: group === role ? '' : group,
            message: role || '',
            type: 'info',
          },
        ]),
      );
      dispatch(getEventType({ eventTypeId, companyId }));
      actions.resolve(null);
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(showError('Не удалось удалить участника'));
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCatalogCollections = createAsyncThunk<
  GetCollectionResponse,
  GetCollectionsQuery,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCatalogCollections',
  async (getCollectionsDto, { rejectWithValue, extra: { inject } }) => {
    try {
      const eventTypeService = inject(EventTypeService);

      const result = await eventTypeService.getCollections({
        query: getCollectionsDto,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCatalogEventTypes = createAsyncThunk<
  GetCompanyCollectionsEventTypesResponse,
  { company_id: string; collection_id: string },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCatalogEventTypes',
  async (
    { company_id, collection_id },
    { rejectWithValue, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(EventTypeService);

      const result = await eventTypeService.getCompanyEventTypesByCollection({
        query: {
          company_id,
        },
        params: {
          collection_id,
        },
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateCatalogEventTypes = createAsyncThunk<
  void,
  {
    company_id: string;
    collection_id: string;
    body: { event_types: EventType[]; collection: string };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateCatalogEventTypes',
  async (
    { company_id, collection_id, body },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(EventTypeService);

      await eventTypeService.updateEventTypesByCollection({
        query: {
          company_id,
        },
        params: {
          collection_id,
        },
        body: {
          event_types: body.event_types,
        },
      });

      dispatch(
        showCustomNotification([
          {
            title: 'Коллекция успешно изменена',
            message: body.collection,
            type: 'info',
          },
        ]),
      );

      dispatch(
        getCatalogEventTypes({
          company_id,
          collection_id,
        }),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось сохранить коллекцию',
            message: 'Попробуйте еще раз',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsEventTypeReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getEventType.pending, (state) => {
    state.eventType.status = 'loading';
    state.eventType.error = null;
    state.eventType.item = null;
  });
  builder.addCase(getEventType.fulfilled, (state, { payload }) => {
    state.eventType.status = 'complete';
    state.eventType.error = null;
    state.eventType.item = payload;
  });
  builder.addCase(getEventType.rejected, (state, { payload, error }) => {
    state.eventType.status = 'failed';
    state.eventType.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
    state.eventType.item = null;
  });

  builder.addCase(updateEventTypeDisabled.fulfilled, (state, { meta }) => {
    if (state.eventType.item) {
      state.eventType.item.event_type.disabled = meta.arg.disabled;
    }
  });

  builder.addCase(getEventTypeNodeOptions.pending, (state) => {
    state.eventType.options = null;
    state.eventType.optionsError = null;
  });
  builder.addCase(getEventTypeNodeOptions.fulfilled, (state, { payload }) => {
    state.eventType.options = payload;
    state.eventType.optionsError = null;
  });
  builder.addCase(
    getEventTypeNodeOptions.rejected,
    (state, { payload, error }) => {
      state.eventType.options = null;
      state.eventType.optionsError =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.eventType.status = 'failed';
    },
  );
  builder.addCase(getEventTypeCollections.pending, (state) => {
    state.eventTypeCollections.items = null;
    state.eventTypeCollections.error = null;
    state.eventTypeCollections.status = 'loading';
  });
  builder.addCase(getEventTypeCollections.fulfilled, (state, { payload }) => {
    state.eventTypeCollections.items = payload.collections;
    state.eventTypeCollections.error = null;
    state.eventTypeCollections.status = 'complete';
  });
  builder.addCase(
    getEventTypeCollections.rejected,
    (state, { payload, error }) => {
      state.eventTypeCollections.items = null;
      state.eventTypeCollections.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.eventTypeCollections.status = 'failed';
    },
  );
  builder.addCase(getParticipantOptions.pending, (state) => {
    state.eventType.participantsOptions = null;
    state.eventType.optionsError = null;
  });
  builder.addCase(getParticipantOptions.fulfilled, (state, { payload }) => {
    state.eventType.participantsOptions = payload.groups;
    state.eventType.participantsOptionsError = null;
  });
  builder.addCase(
    getParticipantOptions.rejected,
    (state, { payload, error }) => {
      state.eventType.participantsOptions = null;
      state.eventType.participantsOptionsError =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.eventType.status = 'failed';
    },
  );
  builder.addCase(addParticipant.fulfilled, (state) => {
    state.eventType.isParticipantsExpanded = true;
  });
  builder.addCase(deleteParticipant.fulfilled, (state) => {
    state.eventType.isParticipantsExpanded = true;
  });

  builder.addCase(getCatalogCollections.pending, (state) => {
    state.collections.status = 'loading';
    state.collections.error = null;
    state.collections.items = [];
  });
  builder.addCase(getCatalogCollections.fulfilled, (state, { payload }) => {
    state.collections.status = 'complete';
    state.collections.error = null;
    state.collections.items = payload.collections;
  });
  builder.addCase(
    getCatalogCollections.rejected,
    (state, { payload, error }) => {
      state.collections.status = 'failed';
      state.collections.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.collections.items = [];
    },
  );

  builder.addCase(getCatalogEventTypes.pending, (state) => {
    state.eventTypesList.status = 'loading';
    state.eventTypesList.error = null;
    state.eventTypesList.items = [];
  });
  builder.addCase(getCatalogEventTypes.fulfilled, (state, { payload }) => {
    state.eventTypesList.status = 'complete';
    state.eventTypesList.error = null;
    state.eventTypesList.items = payload.event_types || [];
    state.eventTypesList.collectionName = payload.collection.name;
  });
  builder.addCase(
    getCatalogEventTypes.rejected,
    (state, { payload, error }) => {
      state.eventTypesList.status = 'failed';
      state.eventTypesList.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.eventTypesList.items = [];
    },
  );
};
